import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {repeat} from 'lit/directives/repeat.js';
import { globalStyles } from '../global-styles';
import { msg } from '@lit/localize';
import { genki } from '../api';

type QuestionOption = {
  label: string;
  value: string;
};

type QuestionConfig = {
  id: string;
  title: string;
  multiSelect: boolean;
  options: QuestionOption[];
};

@customElement('pp-feedback')
export class PpFeedback extends LitElement {
  static styles = [
    globalStyles,
    css`
      :host {
        display: block;
        color: rgba(0, 0, 0 , 0.85);
      }

      .entry {
        color: var(--pp-checkout-black-65);
      }

      .entry span {
        text-decoration: underline;
      }

      .overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.2);
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0;
        z-index: 999;
        pointer-events: none;
        transition: opacity 0.1s ease-in-out;
      }

      .overlay.visible {
        opacity: 1;
        pointer-events: auto;
      }

      .dialog {
        width: 400px;
        padding: 2rem;
        border-radius: 8px;
        background: rgb(255, 255, 255);
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        position: relative;
      }

      .dialog-close {
        position: absolute;
        right: 12px;
        top: 12px;
        cursor: pointer;
      }

      .step-indicator {
        margin-bottom: 1rem;
      }

      .option {
        cursor: pointer;
        margin-bottom: 0.5rem;
      }

      input[type="radio"], input[type="checkbox"] {
        width: 1rem;
        height: 1rem;
      }

      textarea {
        width: 100%;
        height: 100px;
        resize: none;
        padding: 0.5rem;
        font-size: 14px;
        border-radius: 4px;
        border: 1px solid rgba(0, 0, 0,  0.08);
      }

      .navigation {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
      }

      button {
        font-size: 14px;
        cursor: pointer;
        border-radius: 8px;
        padding: 0.5rem 1.5rem;
        transition: opacity 0.2s;
      }

      button:disabled {
        color: rgba(0, 0, 0 , 0.45);
        background: rgba(0, 0, 0 , 0.05);
        border: 1px solid rgba(0, 0, 0 , 0.05);
      }

      .primary {
        color: white;
        background: var(--pp-checkout-theme-color);
        border: 1px solid var(--pp-checkout-theme-color);
      }

      .secondary {
        background: rgb(255, 255, 255);
        color: rgba(0, 0, 0 , 0.85);
        border: 1px solid rgba(0, 0, 0,  0.08);
      }

      .thank-you {
        height: 100%;
      }

      .thank-you img {
        width: 48px;
      }

      .thank-you h2 {
        font-size: 1.25rem;
      }

      .success {
        color: #17c883;
      }

      .failed {
        color: #ff4949;
      }

      @media only screen and (min-width: 0) and (max-width: 576px){
        .entry {
          padding: 0 12px;
        }
        
        .dialog {
          width: 90%;
          height: initial;
        }

        .option {
          align-items: flex-start;
          line-height: 18px;
        }
        
        .navigation {
          margin-top: 16px;
        }
      }
    `
  ];

  @property({ type: String }) orderId = '';

  @state() currentStep = 1;

  @state() visible = false;

  @state() loading = false;

  @state() suggestions = '';

  @state() otherDifficult = '';

  @state() selectedAnswers: Record<string, string[]> = {};
  

  // 更新问题配置，包含多选标识
  private questions: QuestionConfig[] = [
    {
      id: 'Q1',
      title: 'Q1. Where did you encounter issues?',
      multiSelect: false,
      options: [
        {value: 'Q1_1', label: 'Entering card information'},
        {value: 'Q1_2', label: 'During payment processing'},
        {value: 'Q1_3', label: 'After payment attempt'}
      ]
    },
    {
      id: 'Q2',
      title: 'Q2. What made it difficult?',
      multiSelect: true,
      options: [
        {value: 'Q2_1', label: 'Unclear instruction/error message'},
        {value: 'Q2_2', label: 'Page response too slow'},
        {value: 'Q2_3', label: 'Too many verification steps'},
        {value: 'Q2_4', label: 'Payment method not available'},
        {value: 'Q2_5', label: 'Other'}
      ]
    },
    {
      id: 'Q3',
      title: 'Q3. What would help?',
      multiSelect: true,
      options: [
        {value: 'Q3_1', label: 'Clearer instructions'},
        {value: 'Q3_2', label: 'Faster page loading'},
        {value: 'Q3_3', label: 'More payment options'},
        {value: 'Q3_4', label: 'Simpler process'},
      ]
    }
  ];

  get needOtherDifficult() {
    return this.currentStep === 2 && this.selectedAnswers['Q2']?.includes('Q2_5');
  }

  // 处理选项选择（支持单选/多选）
  private handleSelect(questionId: string, value: string) {
    const current = [...(this.selectedAnswers[questionId] || [])];
    const question = this.questions.find(q => q.id === questionId)!;

    const newSelection = question.multiSelect 
      ? current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value]
      : [value];

    this.selectedAnswers = {
      ...this.selectedAnswers,
      [questionId]: newSelection.sort((a,b) => a.localeCompare(b))
    };
  }

  // 验证当前步骤是否完成
  private isStepValid() {
    if (this.currentStep === 4) return true; // Q4可选
    
    const currentQuestion = this.questions[this.currentStep - 1];
    const answers = this.selectedAnswers[currentQuestion.id] || [];
    return answers.length > 0;
  }

  private transformParams(params: Record<string, string[]>) {
    return Object.values(params).flat().map(code => {
      if(code === 'Q2_5') {
        return {
          code,
          desc: this.otherDifficult
        }
      }

      return { code }
    });
  }

  // 提交处理
  private async submitFeedback() {
    if(this.loading) return;

    this.loading = true;

    const feedbackInfoList = this.transformParams(this.selectedAnswers);

    if(this.suggestions) {
      feedbackInfoList.push({
        code: 'Q4_1',
        desc: this.suggestions
      })
    }

    const { $response: { code } } = await genki.addFeedback({
      feedbackInfoList,
      orderId: this.orderId,
    });

    this.currentStep = code === 200? 5: 6;

    this.loading = false;
  }

  close() {
    this.visible = false;
    // 延迟清除数据, 避免页面闪烁
    setTimeout(() => {
      this.currentStep = 1;
      this.suggestions = '';
      this.otherDifficult = '';
      this.selectedAnswers = {};
    }, 500);
  }

  open() {
    this.visible = true;
  }

  renderRepeatOptions(currentQuestion: QuestionConfig) {
    const keyFunction = (option: QuestionOption) => option.value + option.label;

    const itemTemplate = (option: QuestionOption) => {
      const selected = this.selectedAnswers[currentQuestion!.id] || [];
      return html`
        <label class="option text-sm flex items-center gap-2">
          <input
            type=${currentQuestion!.multiSelect ? 'checkbox' : 'radio'}
            name=${currentQuestion!.id}
            .checked=${selected.includes(option.value)}
            @change=${() => this.handleSelect(currentQuestion!.id, option.value)}
          >
          ${option.label}
        </label>
        <!-- Q2 选择 Other 时可让用户输入建议 -->
        ${
          this.needOtherDifficult && option.value === 'Q2_5'? html`<textarea
            .value=${this.otherDifficult}
            @input=${(e: Event) => this.otherDifficult = (e.target as HTMLTextAreaElement).value}
            maxlength="300"
            placeholder="Other difficult...">
          </textarea>`: null
        }
      `
    }
    
    return repeat(currentQuestion!.options, keyFunction, itemTemplate)
  }

  renderFeedbackStep() {
    const currentQuestion = this.currentStep <= 3 
      ? this.questions[this.currentStep - 1]
      : null;

    if(this.currentStep === 5) {
      return this.renderSuccessful() 
    }

    if(this.currentStep === 6) {
      return this.renderFailed()
    }

    return html`
      <div class="full-height flex flex-col justify-between">
        <div>
          <div class="step-indicator font-semibold">Report (${this.currentStep}/4)</div>
          
          ${this.currentStep === 4 ? html`
            <div class="question">
              <p class="font-semibold text-sm m-b-1">Q4. Any suggestions? (Optional)</p>
              <textarea
                .value=${this.suggestions}
                @input=${(e: Event) => this.suggestions = (e.target as HTMLTextAreaElement).value}
                maxlength="300"
                placeholder="Your suggestions...">
              </textarea>
            </div>
            ` : html`
              <div class="question">
                <p class="font-semibold text-sm m-b-1">${currentQuestion!.title}</p>
                  ${this.renderRepeatOptions(currentQuestion!)}
              </div>
            `
          }
        </div>

        <div class="navigation m-t-1">
          ${this.currentStep > 1 ? html`
            <button class="secondary" @click=${() => this.currentStep--}>
              Back
            </button>
          ` : ''}
          
          <button
            class="primary"
            ?disabled=${!this.isStepValid()}
            @click=${() => this.currentStep < 4 ? this.currentStep++ : this.submitFeedback()}
          >
            ${this.currentStep === 4 ? 'Submit' : 'Next'}
          </button>
        </div>
      </div>
    `
  }

  renderSuccessful() {
    return html`
      <div class="thank-you flex flex-col items-center">
        <img src="/images/success.svg"/>
        <h2 class="font-medium success m-t-1">Report submitted</h2>
        <p class="text-sm m-t-1">Thank you for helping us improve!</p>
        <button class="primary m-t-2" @click=${this.close}>Done</button>
      </div>
    `
  }

  renderFailed() {
    return html`
      <div class="thank-you flex flex-col items-center">
        <img src="/images/error.svg"/>
        <h2 class="font-medium failed m-t-1">Report failed</h2>
      </div>
    `
  }

  renderFeedback() {
    return html`
      <div class="overlay ${this.visible? 'visible': ''}">
        <div class="dialog">
          <span class="dialog-close flex" @click=${this.close}>
            <svg t="1711345314165" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4561" width="18" height="18"><path d="M556.8 512l281.6 281.6c12.8 12.8 12.8 32 0 44.8s-32 12.8-44.8 0L512 556.8l-281.6 281.6c-12.8 12.8-32 12.8-44.8 0s-12.8-32 0-44.8l281.6-281.6L185.6 230.4c-12.8-12.8-12.8-32 0-44.8s32-12.8 44.8 0l281.6 281.6 281.6-281.6c12.8-12.8 32-12.8 44.8 0s12.8 32 0 44.8L556.8 512z" p-id="4562"></path></svg>
          </span>
          ${ this.renderFeedbackStep() }
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <p class="entry text-xs p-t-1">
        ${msg('Having trouble? ')}
        <span class="cursor-p" @click=${this.open}>${msg('Click to Report')}</span>
      </p>
      ${this.renderFeedback()}
    `
  }
}